import React, { useContext, useEffect, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { postMediaFile } from 'backend/additionalInformation';
import { openBrowseFile } from 'utils/openBrowseFile';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import Text from 'components/Text/Text';

interface IProps {
	imageSrc: Nullable<string>;
	isImageError?: boolean;
	placeholderIcon: IconName;
	maxFileSizeMB: number;
	onUpload: ({ fileId, imageSrc }: { fileId: string; imageSrc: string }) => void;
	onDelete: () => void;
	onSizeExceeded: () => void;
	onInvalidFormat: () => void;
	onUploadError: () => void;
}

export const ImageBlock: React.FC<IProps> = ({
	imageSrc,
	isImageError = false,
	placeholderIcon,
	maxFileSizeMB,
	onUpload,
	onDelete,
	onSizeExceeded,
	onInvalidFormat,
	onUploadError,
}) => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [isBrokenImage, setIsBrokenImage] = useState<boolean>(false);
	const hasImage = !!imageSrc || isImageError;

	useEffect(() => {
		setIsBrokenImage(false);
	}, [imageSrc]);

	const uploadImage = async (files: FileList | null) => {
		const file = files?.[0];

		if (!file) {
			return;
		}

		if (file.size > 1000 * 1000 * maxFileSizeMB) {
			onSizeExceeded();
			return;
		}

		if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
			onInvalidFormat();
			return;
		}

		const formData = new FormData();
		formData.append('file', file, file.name);

		try {
			showLoader();
			const { data } = await postMediaFile({ data: formData });
			onUpload({ fileId: data.fileId, imageSrc: URL.createObjectURL(file) });
		} catch {
			onUploadError();
		} finally {
			hideLoader();
		}
	};

	const handleUploadClick = () => {
		openBrowseFile({ onChoose: uploadImage, accept: '.jpg, .jpeg, .png' });
	};

	const handleImageError = () => {
		setIsBrokenImage(true);
	};

	const renderImage = () => {
		if (isImageError || isBrokenImage) {
			return (
				<div className="h-48 rounded flex items-center justify-center bg-purple-01-new" data-test="ImageError">
					<Text
						translationNameSpace="form"
						translationKey="imageLoadError"
						theme="neutralMedium"
						align="center"
					/>
				</div>
			);
		}

		if (imageSrc) {
			return (
				<img
					className="w-full h-48 rounded object-cover"
					src={imageSrc}
					alt="Photo"
					onError={handleImageError}
				/>
			);
		}

		return (
			<div className="h-48 rounded flex items-center justify-center bg-purple-01-new">
				<Icon name={placeholderIcon} size="smallHero" className="text-purple-03-new" />
			</div>
		);
	};

	return (
		<div className="flex flex-col gap-4" data-test="ImageBlock">
			{renderImage()}
			<div className="flex justify-between gap-4">
				<NewButton
					size="small"
					iconLeft={<Icon name={hasImage ? IconName.refresh : IconName.upload} size="lg" />}
					testId="UploadImageBtn"
					onClick={handleUploadClick}>
					<Text
						translationNameSpace="form"
						translationKey={hasImage ? 'replaceButton' : 'uploadButton'}
						asFragment={true}
					/>
				</NewButton>
				{hasImage && (
					<NewButton
						variant="outline"
						color="red"
						size="small"
						iconLeft={<Icon name={IconName.delete} size="lg" />}
						testId="DeleteImageBtn"
						onClick={onDelete}>
						<Text translationNameSpace="form" translationKey="deleteButton" asFragment={true} />
					</NewButton>
				)}
			</div>
		</div>
	);
};
